import React from 'react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { motion } from 'framer-motion';
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
export function Card({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('bg-surface rounded-xl shadow-sm border border-slate-200/60 overflow-hidden transition-all duration-200 hover:shadow-md hover:-translate-y-[1px]', className)} {...props} data-id="element-66">
      {children}
    </div>;
}
export function Button({
  className,
  variant = 'primary',
  size = 'md',
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'electric' | 'ghost' | 'danger' | 'outline';
  size?: 'sm' | 'md' | 'lg';
}) {
  const variants = {
    primary: 'bg-navy-900 text-white hover:bg-navy-800 border border-transparent',
    electric: 'bg-electric-500 text-white hover:bg-electric-600 border border-transparent',
    ghost: 'bg-transparent text-slate-600 hover:bg-slate-100 border border-transparent',
    danger: 'bg-red-500 text-white hover:bg-red-600 border border-transparent',
    outline: 'bg-transparent text-slate-700 border border-slate-300 hover:bg-slate-50'
  };
  const sizes = {
    sm: 'px-3 py-1.5 text-xs font-medium',
    md: 'px-4 py-2 text-sm font-medium',
    lg: 'px-6 py-3 text-base font-medium'
  };
  return <button className={cn('inline-flex items-center justify-center rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-electric-500 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none', variants[variant], sizes[size], className)} {...props} data-id="element-67">
      {children}
    </button>;
}
export function StatusPill({
  status,
  className,
  pulse = false,
  dot = true
}: {
  status: 'synced' | 'success' | 'skipped' | 'warning' | 'error' | 'failed' | 'ignored' | 'muted' | 'no-match' | 'info' | 'processing' | 'electric';
  className?: string;
  pulse?: boolean;
  dot?: boolean;
}) {
  const styles = {
    synced: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    skipped: 'bg-amber-50 text-amber-700 border-amber-200',
    warning: 'bg-amber-50 text-amber-700 border-amber-200',
    error: 'bg-red-50 text-red-700 border-red-200',
    failed: 'bg-red-50 text-red-700 border-red-200',
    ignored: 'bg-slate-100 text-slate-600 border-slate-200',
    muted: 'bg-slate-100 text-slate-600 border-slate-200',
    'no-match': 'bg-slate-100 text-slate-600 border-slate-200',
    info: 'bg-slate-100 text-slate-600 border-slate-200',
    processing: 'bg-electric-50 text-electric-700 border-electric-200',
    electric: 'bg-electric-50 text-electric-700 border-electric-200'
  };
  const dotColors = {
    synced: 'bg-emerald-500',
    success: 'bg-emerald-500',
    skipped: 'bg-amber-500',
    warning: 'bg-amber-500',
    error: 'bg-red-500',
    failed: 'bg-red-500',
    ignored: 'bg-slate-400',
    muted: 'bg-slate-400',
    'no-match': 'bg-slate-400',
    info: 'bg-slate-400',
    processing: 'bg-electric-500',
    electric: 'bg-electric-500'
  };
  const normalizedStatus = status.toLowerCase() as keyof typeof styles;
  const label = status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ');
  return <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border', styles[normalizedStatus] || styles.info, className)} data-id="element-68">
      {dot && <span className="relative flex h-1.5 w-1.5 mr-1.5" data-id="element-69">
          {pulse && <span className={cn('animate-ping absolute inline-flex h-full w-full rounded-full opacity-75', dotColors[normalizedStatus])} data-id="element-70"></span>}
          <span className={cn('relative inline-flex rounded-full h-1.5 w-1.5', dotColors[normalizedStatus])} data-id="element-71"></span>
        </span>}
      {label}
    </span>;
}
export function SystemBadge({
  system,
  showLabel = true,
  className
}: {
  system: 'bonzo' | 'ghl' | 'five9' | 'salesforce';
  showLabel?: boolean;
  className?: string;
}) {
  const systems = {
    bonzo: {
      color: 'bg-orange-500',
      label: 'Bonzo'
    },
    ghl: {
      color: 'bg-teal-500',
      label: 'GHL'
    },
    five9: {
      color: 'bg-violet-500',
      label: 'Five9'
    },
    salesforce: {
      color: 'bg-sky-500',
      label: 'Salesforce'
    }
  };
  const sys = systems[system];
  if (!sys) return null;
  return <div className={cn('inline-flex items-center', className)} data-id="element-72">
      <span className={cn('h-2 w-2 rounded-full', sys.color)} data-id="element-73" />
      {showLabel && <span className="ml-1.5 text-xs font-medium text-slate-700" data-id="element-74">
          {sys.label}
        </span>}
    </div>;
}
export function Input({
  className,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn('flex h-9 w-full rounded-md border border-slate-300 bg-white px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-electric-500 disabled:cursor-not-allowed disabled:opacity-50', className)} {...props} data-id="element-75" />;
}
export function ProgressBar({
  progress,
  label,
  eta,
  active = false
}: {
  progress: number;
  label?: string;
  eta?: string;
  active?: boolean;
}) {
  return <div className="w-full" data-id="element-76">
      {(label || eta) && <div className="flex justify-between text-xs mb-1.5" data-id="element-77">
          {label && <span className="font-medium text-slate-700" data-id="element-78">{label}</span>}
          {eta && <span className="text-slate-500 font-mono" data-id="element-79">{eta}</span>}
        </div>}
      <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-80">
        <motion.div className={cn('h-full bg-electric-500 rounded-full', active && 'relative overflow-hidden')} initial={{
        width: 0
      }} animate={{
        width: `${progress}%`
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} data-id="element-81">
          {active && <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(255,255,255,0.15)_25%,transparent_25%,transparent_50%,rgba(255,255,255,0.15)_50%,rgba(255,255,255,0.15)_75%,transparent_75%,transparent)] bg-[length:1rem_1rem] animate-[stripe_1s_linear_infinite]" data-id="element-82" />}
        </motion.div>
      </div>
    </div>;
}